import { useState } from "react";
import { BookOpen, Download, ArrowRight, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { metaLead } from "@/lib/pixel";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";

const ASSET = "https://customer-assets.emergentagent.com/job_web-editor-45/artifacts";

const BOOKS = {
  drive: {
    cover: "/ebooks/drive-cover.png",
    pdf: {
      de: `${ASSET}/zsvr71m3_E-Book%201-%20How%20To%20Hit%20Your%20Driver%20Further%20%28Deutsch%29.pdf`,
      en: `${ASSET}/584bi9zl_E-Book%20-%20How%20To%20Hit%20Your%20Driver%20Further%20%28English%29.pdf`,
    },
    title: { de: "Schlage deinen Driver weiter", en: "Hit your driver further" },
    tag: { de: "// Gratis E-Book", en: "// Free e-book" },
    btn: { de: "Gratis E-Book: Mehr Länge mit dem Driver", en: "Free e-book: More distance with the driver" },
    sub: { de: "Bewährte Techniken & Drills für mehr Weite", en: "Proven techniques & drills for more distance" },
    desc: {
      de: "Bewährte Techniken, Schwung-Mechanik und einfache Drills, mit denen Top-Golfer mehr Länge generieren und den Driver mit Selbstvertrauen schlagen.",
      en: "Proven techniques, swing mechanics and simple drills that top golfers use to add distance and hit driver with confidence.",
    },
  },
  spin: {
    cover: "/ebooks/spin-cover.png",
    pdf: {
      de: `${ASSET}/chcj6xco_How%20to%20get%20more%20Backspin%20%28Deutsch%29.pdf`,
      en: `${ASSET}/chcj6xco_How%20to%20get%20more%20Backspin%20%28Deutsch%29.pdf`,
    },
    title: { de: "Mehr Backspin", en: "More backspin" },
    tag: { de: "// Gratis E-Book", en: "// Free e-book" },
    btn: { de: "Gratis E-Book: Mehr Backspin", en: "Free e-book: More backspin" },
    sub: { de: "So erzeugst du mehr Spin & Kontrolle", en: "How to generate more spin & control" },
    desc: {
      de: "Lerne, wie du mit der richtigen Technik mehr Backspin erzeugst, deine Annäherungen stoppst und mehr Kontrolle ums Grün gewinnst.",
      en: "Learn how the right technique creates more backspin, stops your approach shots and gives you more control around the green.",
    },
  },
};

const EbookDownload = ({ variant = "drive", lang = "de", delay = 0 }) => {
  const book = BOOKS[variant];
  const de = lang === "de";
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", email: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const pdfUrl = book.pdf[lang] || book.pdf.de;

  const triggerDownload = () => {
    const a = document.createElement("a");
    a.href = pdfUrl;
    a.target = "_blank";
    a.rel = "noopener";
    a.download = "";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/ebook-lead", { ...form, ebook: variant, lang });
      metaLead({ contentName: `E-Book Download (${variant || "unknown"})`, source: "ebook", email: form.email });
      setDone(true);
      triggerDownload();
      toast.success(de ? "Download startet – viel Spaß beim Lesen!" : "Download starting – enjoy the read!");
    } catch (err) {
      toast.error(de ? "Etwas ist schiefgelaufen. Bitte erneut versuchen." : "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = (o) => {
    setOpen(o);
    if (!o) {
      setDone(false);
      setForm({ name: "", phone: "", email: "" });
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid={`ebook-${variant}-open-btn`}
        className="group h-full w-full text-left bg-[#0F0F0F] hover:bg-[#141414] transition-colors p-7 md:p-8 flex flex-col"
      >
        <BookOpen size={34} className="text-[#ff0033]" />
        <span className="block font-mono-lab text-[10px] tracking-[0.2em] text-[#ff0033] uppercase mt-6">{book.tag[lang]}</span>
        <span className="block font-heading text-2xl md:text-3xl uppercase tracking-tight text-white mt-1.5 leading-none group-hover:text-[#ff0033] transition-colors">
          {book.btn[lang]}
        </span>
        <span className="block text-neutral-400 text-sm mt-4 leading-relaxed flex-1">{book.sub[lang]}</span>
        <span className="inline-flex items-center gap-1.5 text-[#ff0033] font-heading text-xs uppercase tracking-wider mt-6 group-hover:gap-2.5 transition-all">
          {de ? "Gratis herunterladen" : "Download free"} <Download size={15} />
        </span>
      </button>

      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="max-w-lg bg-[#0B0B0B] border-2 border-[#ff0033] text-white max-h-[90vh] overflow-y-auto" data-testid={`ebook-${variant}-modal`} style={{ boxShadow: "0 0 40px rgba(255,0,51,0.35)" }}>
          <DialogTitle className="sr-only">{book.title[lang]}</DialogTitle>
          <div className="flex gap-5 items-start">
            <img src={book.cover} alt={book.title[lang]} className="w-24 md:w-28 h-auto border border-white/10 shrink-0" data-testid={`ebook-${variant}-cover`} />
            <div className="min-w-0">
              <span className="font-mono-lab text-[10px] tracking-[0.2em] text-[#ff0033] uppercase">{book.tag[lang]}</span>
              <h2 className="font-heading text-2xl md:text-3xl uppercase tracking-tight text-white mt-1 leading-none">{book.title[lang]}</h2>
              <p className="text-neutral-400 text-sm mt-3 leading-relaxed">{book.desc[lang]}</p>
            </div>
          </div>

          {done ? (
            <div className="mt-6 text-center py-6" data-testid={`ebook-${variant}-success`}>
              <div className="w-14 h-14 mx-auto flex items-center justify-center bg-[#ff0033]/15 border border-[#ff0033]/50 text-[#ff0033] rounded-full">
                <Check size={28} />
              </div>
              <p className="text-white font-heading text-xl uppercase tracking-tight mt-4">
                {de ? "Download gestartet!" : "Download started!"}
              </p>
              <p className="text-neutral-400 text-sm mt-2">
                {de ? "Falls der Download nicht startet, hier klicken:" : "If the download didn't start, click here:"}
              </p>
              <button onClick={triggerDownload} className="mt-3 inline-flex items-center gap-2 text-[#ff0033] font-heading uppercase tracking-wider text-sm hover:underline">
                <Download size={16} /> {de ? "E-Book herunterladen" : "Download e-book"}
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-6 space-y-3" data-testid={`ebook-${variant}-form`}>
              <input
                type="text" required value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder={de ? "Vollständiger Name" : "Full name"}
                data-testid={`ebook-${variant}-name`}
                className="w-full bg-[#0F0F0F] border border-white/15 px-4 py-3 text-white placeholder:text-neutral-500 focus:border-[#ff0033] focus:outline-none transition-colors"
              />
              <input
                type="tel" required value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder={de ? "Telefonnummer" : "Phone number"}
                data-testid={`ebook-${variant}-phone`}
                className="w-full bg-[#0F0F0F] border border-white/15 px-4 py-3 text-white placeholder:text-neutral-500 focus:border-[#ff0033] focus:outline-none transition-colors"
              />
              <input
                type="email" required value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder={de ? "E-Mail-Adresse" : "Email address"}
                data-testid={`ebook-${variant}-email`}
                className="w-full bg-[#0F0F0F] border border-white/15 px-4 py-3 text-white placeholder:text-neutral-500 focus:border-[#ff0033] focus:outline-none transition-colors"
              />
              <p className="text-neutral-500 text-xs leading-relaxed">
                {de
                  ? "Mit der Anmeldung zum kostenlosen golfcoaching.online Newsletter erhältst du sofort dein E-Book. Abmeldung jederzeit möglich."
                  : "By signing up for the free golfcoaching.online newsletter you get your e-book instantly. Unsubscribe anytime."}
              </p>
              <button
                type="submit" disabled={loading}
                data-testid={`ebook-${variant}-submit`}
                className="w-full inline-flex items-center justify-center gap-3 bg-[#ff0033] text-white font-heading uppercase tracking-wider px-8 py-4 text-lg hover:bg-[#cc0029] transition-colors disabled:opacity-60"
              >
                {loading ? <Loader2 size={20} className="animate-spin" /> : <>{de ? "Anmelden & herunterladen" : "Sign up & download"} <ArrowRight size={20} /></>}
              </button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default EbookDownload;
